import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, Trophy, ShieldCheck, Clock, ThumbsUp, Wrench, Phone, ChevronDown, ChevronUp, Star, Gift, Calendar, Percent, CheckCircle, MapPin, Award, Users, Home, Sparkles, CloudRain } from "lucide-react";
import { SEOHead, FAQSchema, ReviewSchema } from "@/components/SEO";
import { OCTASKER_BUSINESS } from "@/data/octaskerBusinessInfo";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { trackClickToCall } from "@/utils/analytics";
import PromoBanner from "@/components/PromoBanner";
import { SeasonalBanner } from "@/components/SeasonalBanner";
import RecentEstimates from "@/components/RecentEstimates";
import MyHomeCarousel from "@/components/MyHomeCarousel";
import { GoogleReviewsCarousel } from "@/components/GoogleReviewsCarousel";
import { TrustBadgeTicker } from "@/components/TrustBadgeTicker";
import { HomepageFAQ } from "@/components/HomepageFAQ";
import { InlineEstimateForm } from "@/components/InlineEstimateForm";
import { useReviewStats } from "@/hooks/useReviewStats";

const API = process.env.REACT_APP_BACKEND_URL;

const HomePage = ({ user, onShowAuth, onShowRegister }) => {
  const navigate = useNavigate();
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [expandedTier, setExpandedTier] = useState(null);
  const [settings, setSettings] = useState({ new_account_credit: 25 });
  const { ratingLabel, totalPlusLabel } = useReviewStats();

  // Fetch settings for configurable values
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/api/gift-cards/amounts`);
        // We'll use a public endpoint to get some settings
      } catch (error) {
      }
    };
    fetchSettings();
  }, []);

  const features = [
    { 
      icon: Zap, 
      title: "Fast & Easy Booking", 
      shortDesc: "Book your service online in under 60 seconds.",
      fullDesc: "Skip the phone tag and endless voicemails. Our streamlined booking system lets you schedule your handyman service in under 60 seconds. Pick your preferred time slot, describe your project, upload photos if needed, and we'll handle the rest. Real-time calendar availability means no more waiting for callbacks—just instant confirmation and peace of mind.",
      color: "from-orange-500 to-amber-500"
    },
    { 
      icon: Trophy, 
      title: "Earn Cashback", 
      shortDesc: "Free to join. Every job earns you cashback toward your next service.",
      fullDesc: "Done Right Rewards is 100% free — no signup fee, no annual fee. You're auto-enrolled the moment you book your first job. Every dollar of labor you spend earns cashback that goes straight onto your account, ready to apply to your next service. Start at 5% back as a Bronze member; reach Silver ($1,500 lifetime spend) for 7% back + priority scheduling; Gold members ($5,000+) get 10% back + VIP scheduling + a free trip charge. Refer friends and earn 10% of their first job too.",
      color: "from-green-500 to-emerald-500"
    },
    { 
      icon: ShieldCheck, 
      title: "Trusted Professionals", 
      shortDesc: "Background-checked, insured, and 5-star rated technicians.",
      fullDesc: "Your home deserves the best. Every OC Tasker technician undergoes comprehensive background checks, carries full liability insurance, and maintains a 5-star rating from homeowners like you. We treat your home with the same care we'd give our own—clean workspaces, quality materials, and craftsmanship you can trust. Satisfaction guaranteed or we'll make it right.",
      color: "from-blue-500 to-indigo-500"
    },
    { 
      icon: Award, 
      title: "1-Year Service Guarantee", 
      shortDesc: "All work backed by our comprehensive 1-year warranty.",
      fullDesc: "We stand behind every job we complete. If any issue arises with our workmanship within one year of service, we'll come back and make it right at no additional cost. That's our promise to you—quality work that lasts, backed by a guarantee you can trust.",
      color: "from-purple-500 to-violet-500"
    },
  ];

  const tiers = [
    { 
      name: "Bronze", 
      range: "$0–$1,499 spent", 
      shortBenefit: "5% cashback on every job",
      icon: Star,
      color: "from-orange-400 to-amber-500",
      fullBenefits: [
        "Free to join — auto-enrolled with your first booking",
        "Earn 5% cashback on every completed job ($5 back per $100 of labor)",
        "Apply cashback directly to your next service",
        "$25 signup credit when you create your account",
        "Refer a friend: 10% cashback on their first job, plus $25 credit",
        "Birthday bonus: $25 cashback every year on your birthday",
        "Cashback never expires while you're an active customer"
      ],
      cta: "Start earning with your first service — it's automatic!"
    },
    { 
      name: "Silver", 
      range: "$1,500–$4,999 spent", 
      shortBenefit: "7% cashback + priority scheduling",
      icon: Calendar,
      color: "from-slate-400 to-gray-500",
      fullBenefits: [
        "All Bronze benefits included",
        "7% cashback on every job ($7 back per $100 of labor — 40% more than Bronze)",
        "Priority scheduling — get first pick of available time slots",
        "Early access to seasonal promotions"
      ],
      cta: "Reach Silver at $1,500 lifetime spend — automatic upgrade."
    },
    { 
      name: "Gold", 
      range: "$5,000+ spent", 
      shortBenefit: "10% cashback + VIP scheduling",
      icon: Gift,
      color: "from-yellow-400 to-amber-500",
      fullBenefits: [
        "All Silver benefits included",
        "10% cashback on every job ($10 back per $100 of labor — 2× Bronze rate)",
        "Free trip charge on your next job (one-time Gold milestone)",
        "VIP scheduling — first choice of every time slot",
        "Exclusive Gold-only promotions",
        "Early access to new services"
      ],
      cta: "Our most valued customers enjoy the Gold experience."
    },
  ];

  const stats = [
    { value: "8+", label: "Years Experience" },
    { value: "5,000+", label: "Jobs Completed" },
    { value: `${ratingLabel}★`, label: `from ${totalPlusLabel} Reviews`, link: "/reviews" },
    { value: "100%", label: "Satisfaction" },
  ];

  // Testimonials — curated fallback pool used only when the live Google
  // reviews API is unreachable. Real 5-star reviews are fetched below and
  // rotated deterministically every 5 days.
  const testimonials = [
    { name: "Sarah M.", location: "Newport Beach", text: "Incredible service! The technician arrived on time, was super professional, and my TV mount looks perfect. Will definitely use again!", rating: 5 },
    { name: "Mike R.", location: "Irvine", text: "Best handyman service in OC. They installed my smart home system flawlessly. The booking process was so easy!", rating: 5 },
    { name: "Jennifer L.", location: "Costa Mesa", text: "Fast, reliable, and reasonably priced. They fixed multiple things around my house in one visit. Highly recommend!", rating: 5 },
    { name: "David K.", location: "Huntington Beach", text: "The rewards program is a great bonus! Already earned enough points for a discount on my next service.", rating: 5 },
    { name: "Amanda P.", location: "Laguna Beach", text: "Professional, courteous, and thorough. They even cleaned up after themselves. 5 stars all the way!", rating: 5 },
  ];

  // Rotating hero testimonial — pulls the latest 5-star Google reviews
  // (already synced every 2h by scheduler.py) and rotates through them on
  // a strict 5-day cadence using days-since-epoch as the deterministic
  // index. All visitors on the same UTC day see the same review (helps
  // SEO consistency + word-of-mouth: "I saw a review on their site from
  // Jane"). Falls back to the curated `testimonials` pool if the API is
  // unreachable so the hero never renders empty.
  const [liveReviews, setLiveReviews] = useState([]);
  const [quizStats, setQuizStats] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/google-reviews/public`)
      .then(res => {
        if (!alive) return;
        const rvs = (res.data?.reviews || []).filter(r => (r.rating || 0) >= 5 && (r.text || "").length > 40);
        setLiveReviews(rvs);
      })
      .catch(() => { /* silently fall back to curated pool */ });
    // Live quiz activity counter — powers the pulsing dot under the CTA
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/scorecard/live-stats`)
      .then(res => { if (alive) setQuizStats(res.data || null); })
      .catch(() => { /* hide counter if endpoint fails */ });
    return () => { alive = false; };
  }, []);
  const rotatingReview = useMemo(() => {
    const pool = liveReviews.length > 0
      ? liveReviews.map(r => ({ name: r.author_name || r.reviewer_name || "Verified Google customer", location: "", text: r.text }))
      : testimonials;
    const daysSinceEpoch = Math.floor(Date.now() / 86400000);
    const bucket = Math.floor(daysSinceEpoch / 5);   // rotates every 5 days
    return pool[bucket % pool.length];
  }, [liveReviews, testimonials]);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Newport Beach Handyman Services | Free Estimate"
        description="Free Estimates. 5-Star Rated. Fully Insured. OC Tasker is Orange County's #1 Handyman Service. Home repair, TV mounting, drywall, plumbing, electrical, painting & property maintenance. Call (949) 204-0301."
        path="/"
        aiSummary="OC Tasker is a 5-star, fully insured handyman service in Newport Beach, CA serving Orange County (Newport Beach, Costa Mesa, Huntington Beach, Irvine, Laguna Beach). Free estimates, transparent labor + materials pricing, and a Done Right Rewards program with 5–10% cashback. Services include TV mounting, smart home installation, drywall repair, painting, plumbing, electrical, pressure washing, and property maintenance plans. Phone: (949) 204-0301. Book free estimate at https://octasker.com/get-estimate."
      />
      {/* Curated customer reviews schema for AI agents to quote directly */}
      <ReviewSchema
        reviews={testimonials.map((t, i) => ({
          author: t.name,
          datePublished: ["2026-04-12", "2026-04-18", "2026-04-25", "2026-05-03", "2026-05-12"][i] || "2026-05-01",
          rating: t.rating,
          text: t.text,
        }))}
      />
      {/* Promotional Banner - Shows when there's an active promotion */}
      <PromoBanner />
      <SeasonalBanner />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="hero-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="py-8 sm:py-12 lg:py-20 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Logo and Company Name */}
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 justify-center lg:justify-start">
                <img
                  src="/static/img/oc-tasker-logo-96.v2.webp"
                  srcSet="/static/img/oc-tasker-logo-96.v2.webp 96w, /static/img/oc-tasker-logo-160.v2.webp 160w"
                  sizes="(min-width: 1024px) 80px, (min-width: 640px) 64px, 48px"
                  alt="OC Tasker Logo"
                  width="80"
                  height="80"
                  className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl shadow-lg logo-hover-spin"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                <div>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">OC Tasker</h2>
                  <p className="text-orange-400 text-xs sm:text-sm font-medium">Your Home. Our Priority.</p>
                </div>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 sm:mb-4 leading-tight">
                Home Repair, Maintenance
                <span className="block bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                  & Improvement Experts
                </span>
              </h1>
              
              <p className="text-base sm:text-lg text-gray-300 mb-4 sm:mb-5 max-w-xl mx-auto lg:mx-0">
                Orange County's #1 rated handyman service. From drywall and plumbing to painting, electrical, TV mounting, and full property maintenance — done right, guaranteed.
              </p>

              {/* Trust Badges — prominent */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-5 sm:mb-6 justify-center lg:justify-start">
                <button onClick={() => navigate("/reviews")} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 hover:bg-white/15 transition-colors">
                  <div className="flex">
                    {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                  </div>
                  <span className="text-white font-bold text-base">{ratingLabel}</span>
                  <span className="text-gray-300 text-sm font-medium">from {totalPlusLabel} Reviews</span>
                </button>
                <div className="flex items-center gap-2 bg-green-500/15 border border-green-500/30 rounded-full px-4 py-2">
                  <ShieldCheck className="w-4 h-4 text-green-400" />
                  <span className="text-green-300 text-sm font-semibold">Fully Insured</span>
                </div>
              </div>

              {/* 1-Year Guarantee Badge — prominent */}
              <div className="mb-5 sm:mb-6 flex justify-center lg:justify-start">
                <div className="relative inline-flex items-center gap-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-2 border-amber-400/40 rounded-2xl px-5 py-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30 flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <span className="text-white font-bold text-base sm:text-lg block leading-tight">1-Year Workmanship Guarantee</span>
                    <span className="text-amber-300/80 text-xs">Every job backed by our quality promise</span>
                  </div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 lg:justify-start justify-center">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full shadow-lg shadow-orange-500/40 font-semibold min-h-[56px]"
                  onClick={() => navigate("/get-estimate")}
                  data-testid="hero-get-estimate-primary"
                >
                  Get My Free Estimate
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>

              {/* Secondary CTA moved to the right column, below the hero
                  image. Keeps the primary "Get My Free Estimate" button as
                  the visual focal point in the left column while giving cold
                  visitors a discoverable soft entry after they've seen the
                  hero image + call CTA. */}
              
              {/* Reassurance */}
              <div className="mt-3 sm:mt-4 space-y-1 text-center lg:text-left">
                <p className="text-gray-400 text-xs sm:text-sm" data-testid="hero-pricing-note">Free estimates. No obligation. Service visits start at $299 · individual tasks from $99 within your visit.</p>
              </div>

              {/* Mini Review Quote — rotates every 5 days across the most
                  recent real Google 5-star reviews (from google_reviews DB,
                  synced every 2h). Date-based deterministic index so all
                  visitors on the same day see the same testimonial (good
                  for SEO + shareability). Falls back to a curated static
                  testimonial if the API is unreachable. */}
              <div className="mt-5 sm:mt-7 bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4 max-w-lg mx-auto lg:mx-0">
                <div className="flex items-start gap-3">
                  <div className="flex mt-0.5">
                    {[1,2,3,4,5].map(i => <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />)}
                  </div>
                  <div>
                    <p className="text-gray-300 text-xs sm:text-sm italic leading-relaxed" data-testid="hero-rotating-review-text">"{rotatingReview.text}"</p>
                    <p className="text-gray-500 text-xs mt-1" data-testid="hero-rotating-review-attribution">— {rotatingReview.name}{rotatingReview.location ? `, ${rotatingReview.location}` : ""}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Image + Call CTA. Same DOM node for mobile & desktop —
                grid.lg:grid-cols-2 reflows this from stacked-below-text
                on mobile to right-column on desktop. Prevents the duplicate
                800w image download that PageSpeed flagged (was 306 KB
                wasted, now 153 KB or 68 KB depending on viewport). */}
            <div className="relative">
              {/* Call Button above image */}
              <a
                href="tel:+19492040301"
                onClick={trackClickToCall}
                className="relative z-10 mb-4 w-full max-w-lg mx-auto flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-4 rounded-xl shadow-lg shadow-green-500/30 font-semibold text-lg transition-all"
                data-testid="hero-call-now"
              >
                <Phone className="w-5 h-5" />
                Call (949) 204-0301
              </a>
              <div className="absolute -inset-4 top-8 bg-gradient-to-r from-orange-500 to-green-500 rounded-3xl blur-2xl opacity-20 hidden lg:block"></div>
              <picture>
                <source
                  type="image/webp"
                  srcSet="/static/img/oc-technician-480.v2.webp 480w, /static/img/oc-technician-640.v2.webp 640w, /static/img/oc-technician-800.v2.webp 800w"
                  sizes="(max-width: 1023px) 92vw, 500px"
                />
                <img decoding="async"
                  src="/static/img/oc-technician-480.v2.webp"
                  alt="OC Tasker Professional Technician"
                  width="480"
                  height="720"
                  className="relative rounded-3xl shadow-2xl w-full max-w-lg mx-auto"
                  loading="eager"
                />
              </picture>
              {/* Scorecard soft CTA — placed under the hero image (right
                  column) intentionally: hot buyers already see the primary
                  "Get My Free Estimate" button in the left column, so this
                  is a discovery path for cold/undecided visitors who
                  scrolled past the primary CTA.
                  Design (Option 3): frosted-glass pill with an orange
                  ring; sparkle icon pulses gently to attract the eye
                  without stealing attention from the primary CTA. Live
                  activity counter (pulsing green dot) sits underneath. */}
              <div className="mt-5 max-w-lg mx-auto">
                <button
                  type="button"
                  onClick={() => navigate("/quiz")}
                  data-testid="hero-quiz-secondary"
                  className="group relative w-full flex items-center gap-3 sm:gap-4 rounded-2xl bg-white/10 hover:bg-white/[0.14] backdrop-blur-md border border-orange-400/60 hover:border-orange-300 text-white px-4 sm:px-5 py-3.5 sm:py-4 shadow-lg shadow-black/30 transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  {/* Icon block: sparkle + home stacked inside a soft
                      orange glow. Sparkle pulses via Tailwind's
                      animate-pulse for gentle eye-catch. */}
                  <span className="relative flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-orange-500/30 to-orange-600/20 shrink-0 ring-1 ring-orange-400/40">
                    <Home className="w-5 h-5 sm:w-6 sm:h-6 text-orange-300" strokeWidth={2.4} />
                    <Sparkles className="absolute -top-1 -right-1 w-3.5 h-3.5 text-yellow-300 animate-pulse drop-shadow-[0_0_6px_rgba(253,224,71,0.9)]" strokeWidth={2.6} />
                  </span>

                  {/* Text stack */}
                  <span className="flex-1 text-left min-w-0">
                    <span className="block text-sm sm:text-base font-bold leading-tight text-white">
                      Get Your Free Home Repair Score
                    </span>
                    <span className="block text-[11px] sm:text-xs text-orange-200/80 mt-0.5 tracking-wide">
                      7 questions · 60 seconds · personalized
                    </span>
                  </span>

                  {/* Trailing arrow (slides right on hover) */}
                  <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 shrink-0 text-orange-300 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.6} />
                </button>

                {/* Live activity counter — social proof + FOMO. Shows the
                    pulsing green dot; message tunes to 24h vs 7d density
                    and appends "last score: N" when a recent decent
                    scorer exists. */}
                {quizStats && (
                  <div className="mt-2.5 flex items-center justify-center gap-2 text-xs text-slate-300" data-testid="hero-quiz-live-counter">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                    </span>
                    <span>
                      {quizStats.count_24h > 0
                        ? <><strong className="text-green-300">{quizStats.count_24h}</strong> {quizStats.count_24h === 1 ? "person" : "people"} scored in the last 24 hours</>
                        : quizStats.count_7d > 0
                          ? <><strong className="text-green-300">{quizStats.count_7d}</strong> {quizStats.count_7d === 1 ? "neighbor" : "neighbors"} scored this week</>
                          : <>Be the <strong className="text-green-300">first</strong> to get your score this week</>
                      }
                      {typeof quizStats.last_score === "number" && (
                        <> · <span className="text-slate-400">last score:</span> <strong className="text-yellow-300">{quizStats.last_score}</strong></>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recently Submitted Estimates - Shows for guests who have recently submitted estimates */}
      <RecentEstimates />

      {/* My Home Carousel — quick-book dashboard for authenticated customers */}
      <MyHomeCarousel user={user} />

      {/* Trust Badge Ticker */}
      <TrustBadgeTicker />

      {/* How It Works */}
      <section className="py-12 sm:py-16 bg-white" data-testid="how-it-works-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              How It Works
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
              From first call to finished job — here's what to expect
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {[
              { step: "1", title: "Request Your Estimate", desc: "Tell us about your project online or call (949) 204-0301. It takes under 60 seconds.", icon: Phone, color: "from-orange-500 to-amber-500" },
              { step: "2", title: "Fast Response", desc: "We review your request and respond with a clear, honest estimate — most within 30 minutes.", icon: Clock, color: "from-blue-500 to-indigo-500" },
              { step: "3", title: "We Arrive & Get to Work", desc: "Our vetted, insured technician arrives on time with the right tools and materials. No surprises.", icon: Wrench, color: "from-green-500 to-emerald-500" },
              { step: "4", title: "Satisfaction Guaranteed", desc: "We clean up, walk you through the work, and back it with our 1-year workmanship guarantee.", icon: CheckCircle, color: "from-purple-500 to-violet-500" },
            ].map((item, idx) => (
              <div key={idx} className="relative text-center group">
                {/* Connector line (hidden on first item and mobile) */}
                {idx > 0 && (
                  <div className="hidden lg:block absolute top-8 -left-3 w-6 border-t-2 border-dashed border-gray-200"></div>
                )}
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-105 transition-transform`}>
                  <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="text-xs font-bold text-orange-700 mb-1">STEP {item.step}</div>
                <h3 className="font-bold text-gray-900 text-sm sm:text-base mb-1.5">{item.title}</h3>
                <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-5 text-base rounded-full shadow-lg font-semibold"
              onClick={() => navigate("/get-estimate")}
              data-testid="how-it-works-cta"
            >
              Get My Free Estimate
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white" data-testid="features-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Why Choose OC Tasker?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
              Experience the difference with Orange County's most trusted handyman service
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="group relative bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 hover:border-orange-200 shadow-sm hover:shadow-xl transition-all duration-300"
                data-testid={`feature-${idx}`}
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-5 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed text-xs sm:text-sm mb-3">{feature.shortDesc}</p>
                
                {/* Expandable Content */}
                {expandedFeature === idx && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-gray-600 leading-relaxed text-xs sm:text-sm">{feature.fullDesc}</p>
                  </div>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 text-orange-700 border-orange-200 hover:bg-orange-50 hover:border-orange-300 text-xs sm:text-sm min-h-[40px]"
                  onClick={() => setExpandedFeature(expandedFeature === idx ? null : idx)}
                  data-testid={`feature-${idx}-toggle`}
                >
                  {expandedFeature === idx ? (
                    <>Hide Details <ChevronUp className="ml-1 w-4 h-4" /></>
                  ) : (
                    <>View Details <ChevronDown className="ml-1 w-4 h-4" /></>
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews — custom carousel driven by our /api/google-reviews/public
          endpoint. Replaced the Elfsight widget here because it silently failed on
          iOS mobile ~10-15% of the time. Elfsight is still used on /reviews for the
          full scrollable feed. */}
      <GoogleReviewsCarousel />

      {/* Property Health Check CTA Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden" data-testid="health-check-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left Content */}
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-orange-500/20 border border-orange-500/30 mb-4">
                  <ShieldCheck className="w-4 h-4 text-orange-400 mr-2" />
                  <span className="text-orange-400 text-sm font-medium">Free Diagnostic Tool</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Property Health Check
                </h2>
                <p className="text-gray-300 text-lg mb-6">
                  See how your home ranks and identify maintenance risks before they become expensive repairs.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Get your Property Health Score (0-100)</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Compare to Orange County averages</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Discover likely maintenance issues</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Download your personalized report</span>
                  </li>
                </ul>
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-6">
                  <Clock className="w-4 h-4" />
                  <span>About 60 seconds to complete</span>
                </div>
              </div>
              
              {/* Right CTA Card */}
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <ShieldCheck className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Get Your Free Score</h3>
                  <p className="text-gray-600 text-sm mb-6">
                    Answer a few questions about your property and receive your personalized health assessment.
                  </p>
                  <Button
                    size="lg"
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-orange-500/30 font-semibold"
                    onClick={() => navigate("/property-health-check")}
                    data-testid="health-check-cta"
                  >
                    Start Property Health Check
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <p className="text-xs text-gray-500 mt-4">No account required. 100% free.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Most Popular Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From electrical fixtures to complete property prep, our expert technicians 
              deliver clean, precise work with excellent results.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="space-y-4">
                {[
                  { icon: CloudRain, text: "StormReady™ Flood Barriers & Sandbags", desc: "Delivered & installed · pick-up extra", price: "Free estimate", link: "/services/flood-barrier-rental", testId: "home-service-stormready" },
                  { icon: Zap, text: "TV Mounting & Installation", desc: "Professional wall mounting for any TV size", price: "from $199", link: "/services/tv-mounting-service" },
                  { icon: Zap, text: "Electrical Fixtures", desc: "Light fixtures, ceiling fans, outlets & switches", price: "from $99", link: "/services/electrical" },
                  { icon: Wrench, text: "Drywall & Painting", desc: "Patch repairs, texture matching & touch-ups", price: "from $199", link: "/services/drywall-repair-and-patching" },
                  { icon: Wrench, text: "Plumbing", desc: "Faucets, toilets, garbage disposals & fixtures", price: "from $99", link: "/services/plumbing" },
                  { icon: Wrench, text: "Door & Hardware", desc: "Installation, repairs, locks & handles", price: "from $99", link: "/services/door-repair" },
                  { icon: Wrench, text: "Property Prep Services", desc: "AirBnB turnover, real estate sale prep", price: "from $299", link: "/services/home-repair-services" },
                ].map((service, idx) => (
                  <a key={idx} href={service.link} data-testid={service.testId} className="flex items-start space-x-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-orange-200 hover:shadow-md transition-all cursor-pointer">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-900 font-semibold">{service.text}</span>
                        <span className="text-orange-700 font-medium text-sm">{service.price}</span>
                      </div>
                      <p className="text-gray-500 text-sm mt-1">{service.desc}</p>
                    </div>
                  </a>
                ))}
              </div>
              <p className="mt-4 text-sm text-gray-500" data-testid="services-visit-minimum-note">
                {OCTASKER_BUSINESS.minimumJobStatement} Bundle several tasks into one visit to get the most from it.
              </p>

              <Button
                size="lg"
                className="mt-8 bg-orange-700 hover:bg-orange-800 text-white rounded-full"
                onClick={() => navigate("/services")}
              >
                View All 28 Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            <div className="relative lg:sticky lg:top-24">
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-400 to-green-400 rounded-3xl blur-2xl opacity-20"></div>
              <div className="relative bg-white rounded-2xl shadow-xl p-8">
                <div className="text-center mb-6">
                  <div className="text-sm text-gray-500 mb-1">Typical projects start around</div>
                  <div className="text-4xl font-bold text-gray-900">$299</div>
                  <p className="text-xs text-gray-500 mt-2">Exact pricing provided after estimate</p>
                </div>
                
                <Button
                  className="w-full bg-green-700 hover:bg-green-800 text-white rounded-full px-6 py-6 text-lg font-semibold mb-6"
                  onClick={() => navigate("/get-estimate")}
                  data-testid="book-online-save"
                >
                  Book Service Now
                </Button>

                <div className="space-y-3 pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Clock className="w-5 h-5 text-green-500" />
                    <span>Same-day service available</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <ShieldCheck className="w-5 h-5 text-green-500" />
                    <span>Vetted and insured</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <ThumbsUp className="w-5 h-5 text-green-500" />
                    <span>Satisfaction guaranteed</span>
                  </div>
                </div>

                <p className="text-center text-xs text-gray-400 mt-6">Free estimates. No obligation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance Membership — Revenue Engine */}
      <section className="py-14 sm:py-18 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden" data-testid="membership-cta-section">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.3"%3E%3Cpath d="M20 18v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
            {/* Left — Problem + Why */}
            <div>
              <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-red-500/20 border border-red-500/30 mb-4">
                <span className="text-red-400 text-xs font-medium">10,000 house fires yearly from clogged dryer vents</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Stop Ignoring Your Home's Maintenance
              </h2>
              <p className="text-gray-300 mb-6">
                Routine tasks like dryer vent cleaning, HVAC filter changes, and plumbing checks prevent costly repairs and dangerous hazards. Our Property Maintenance Membership handles it all — so you don't have to.
              </p>
              <div className="space-y-3 mb-6">
                {[
                  "Scheduled seasonal visits — we remind you so you don't forget",
                  "Priority scheduling — jump to the front of the line",
                  "Discounted service rates for members",
                  "Annual home health assessment included",
                  "One dedicated technician who knows your home"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Right — CTA Card */}
            <div className="bg-white rounded-2xl p-7 shadow-2xl">
              <div className="text-center mb-5">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-3">
                  <ShieldCheck className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Property Maintenance Membership</h3>
                <p className="text-gray-500 text-sm">Protect your home. Save money. Get peace of mind.</p>
              </div>
              <div className="space-y-3 mb-6">
                {[
                  { label: "Dryer vent cleaning", note: "Prevents fires" },
                  { label: "HVAC filter & system check", note: "Extends equipment life" },
                  { label: "Plumbing inspection", note: "Catches leaks early" },
                  { label: "Smoke & CO detector check", note: "Safety first" },
                ].map((task, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-800 font-medium">{task.label}</span>
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">{task.note}</span>
                  </div>
                ))}
              </div>
              <Button
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-5 text-base rounded-xl font-semibold shadow-lg"
                onClick={() => navigate("/maintenance-membership")}
                data-testid="membership-cta"
              >
                See Membership Plans
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-center text-xs text-gray-400 mt-3">No contract. Cancel anytime. Plans start at $299/month.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Loyalty Tiers Section */}
      <section className="py-20 bg-white" data-testid="loyalty-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Done Right Rewards Program
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-6">
              Earn 5–10% cashback on every completed job — automatically applied to your next service. Free to join, no annual fee. <span className="text-gray-500 text-sm">(Earned on labor only.)</span>
            </p>
            
            {/* Cashback Formula Display */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-8">
              <div className="flex items-center gap-2 bg-orange-50 px-4 py-2 rounded-full">
                <Trophy className="w-5 h-5 text-orange-600" />
                <span className="text-gray-700 font-medium">Earn 5–10% cashback on every job</span>
              </div>
              <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-full">
                <Gift className="w-5 h-5 text-green-600" />
                <span className="text-gray-700 font-medium">FREE to join · No annual fee</span>
              </div>
            </div>

            {/* Signup Incentive - Clickable */}
            <button
              onClick={onShowRegister}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-amber-100 border border-orange-200 px-6 py-3 rounded-full mb-8 hover:from-orange-200 hover:to-amber-200 hover:border-orange-300 transition-all cursor-pointer"
              data-testid="signup-incentive-banner"
            >
              <Star className="w-5 h-5 text-orange-600" />
              <span className="text-gray-800 font-semibold">Create your account and receive $25 credit toward your next service!</span>
            </button>
          </div>

          {/* Visual Progress Indicator */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative">
              <div className="absolute top-1/2 left-0 right-0 h-2 bg-gray-200 rounded-full -translate-y-1/2"></div>
              <div className="absolute top-1/2 left-0 w-1/3 h-2 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full -translate-y-1/2"></div>
              <div className="relative flex justify-between">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center border-4 border-white shadow-md">
                    <Star className="w-5 h-5 text-white" />
                  </div>
                  <span className="mt-2 text-xs font-medium text-gray-600">Bronze</span>
                  <span className="text-xs text-gray-400">$0+</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-400 to-gray-500 flex items-center justify-center border-4 border-white shadow-md">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <span className="mt-2 text-xs font-medium text-gray-600">Silver</span>
                  <span className="text-xs text-gray-400">$1,500+</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center border-4 border-white shadow-md">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <span className="mt-2 text-xs font-medium text-gray-600">Gold</span>
                  <span className="text-xs text-gray-400">$5,000+</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {tiers.map((tier, idx) => (
              <div
                key={idx}
                className="relative group"
                data-testid={`tier-${tier.name.toLowerCase()}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${tier.color} rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity`}></div>
                <div className="relative bg-white p-8 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${tier.color} flex items-center justify-center`}>
                    <tier.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">{tier.range}</p>
                  <p className="text-sm font-medium text-gray-700 mb-4">{tier.shortBenefit}</p>
                  
                  {/* Expandable Benefits */}
                  {expandedTier === idx && (
                    <div className="mt-4 pt-4 border-t border-gray-100 text-left">
                      <ul className="space-y-2 mb-4">
                        {tier.fullBenefits.map((benefit, bidx) => (
                          <li key={bidx} className="flex items-start text-sm text-gray-600">
                            <Star className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-orange-600 font-medium italic">{tier.cta}</p>
                    </div>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    onClick={() => setExpandedTier(expandedTier === idx ? null : idx)}
                    data-testid={`tier-${tier.name.toLowerCase()}-toggle`}
                  >
                    {expandedTier === idx ? (
                      <>Hide Benefits <ChevronUp className="ml-1 w-4 h-4" /></>
                    ) : (
                      <>View Benefits <ChevronDown className="ml-1 w-4 h-4" /></>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Join CTA Button */}
          <div className="text-center mt-10">
            <Button
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-full shadow-lg"
              onClick={() => user ? navigate("/loyalty") : onShowRegister()}
              data-testid="join-rewards-cta"
            >
              <Award className="w-5 h-5 mr-2" />
              {user ? "View My Rewards" : "Join Rewards Program"}
            </Button>
          </div>
        </div>
      </section>

      {/* Service Areas Section with Google Maps */}
      <section className="py-16 bg-gray-50" data-testid="service-areas-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Proudly Serving Orange County</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              OC Tasker is a Newport Beach based handyman and home repair company. We provide our service in the following cities and their surrounding areas.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto items-start">
            {/* City Links */}
            <div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: "Newport Beach", slug: "newport-beach-handyman-services" },
                  { name: "Costa Mesa", slug: "costa-mesa-handyman-services" },
                  { name: "Huntington Beach", slug: "huntington-beach-handyman-services" },
                  { name: "Irvine", slug: "irvine-handyman-services" },
                  { name: "Laguna Beach", slug: "laguna-beach-handyman-services" },
                  { name: "Newport Coast", slug: "newport-coast-handyman-services" },
                  { name: "Corona Del Mar", slug: "corona-del-mar-handyman-services" },
                  { name: "Fountain Valley", slug: "fountain-valley-handyman-services" }
                ].map((city) => (
                  <a
                    key={city.slug}
                    href={`/service-areas/${city.slug}`}
                    className="flex items-center gap-2 p-4 bg-white rounded-lg border border-gray-200 hover:border-orange-400 hover:shadow-md transition-all group"
                  >
                    <MapPin className="w-4 h-4 text-orange-500 shrink-0" />
                    <span className="font-medium text-gray-800 group-hover:text-orange-600 transition-colors text-sm">{city.name}</span>
                  </a>
                ))}
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href="/service-areas" className="text-orange-600 hover:text-orange-700 font-medium text-sm flex items-center gap-1">
                  View all service areas <ArrowRight className="w-4 h-4" />
                </a>
                <span className="text-gray-400 text-sm hidden sm:inline">|</span>
                <a href="tel:+19492040301" onClick={trackClickToCall} className="text-gray-600 hover:text-orange-600 font-medium text-sm flex items-center gap-1">
                  <Phone className="w-4 h-4" /> Don't see your city? Call us!
                </a>
              </div>
            </div>
            {/* Google Maps Embed */}
            <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 h-[350px]">
              <iframe
                title="OC Tasker Service Area - Newport Beach, Orange County"
                src="https://maps.google.com/maps?q=oc%20tasker%20newport%20beach&t=m&z=10&output=embed&iwloc=near"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Inline Estimate Form */}
      <InlineEstimateForm />

      {/* Homepage FAQ */}
      <HomepageFAQ />

      {/* Start Your Project Today - Satisfaction Guaranteed CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden" data-testid="start-project-section">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.3"%3E%3Cpath d="M20 18v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Start your project today</h2>
              <div className="relative inline-block">
                <span className="text-2xl sm:text-3xl font-bold text-orange-400">Satisfaction Guaranteed</span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none" preserveAspectRatio="none">
                  <path d="M0 6 Q75 0 150 6 Q225 12 300 6" stroke="#f97316" strokeWidth="3" fill="none" strokeLinecap="round" />
                </svg>
              </div>
              <p className="text-gray-400 mt-4 text-sm sm:text-base max-w-md">
                Every job backed by our 1-year workmanship guarantee. Free estimates, no obligation.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-orange-500/30 font-semibold whitespace-nowrap"
                onClick={() => navigate("/get-estimate")}
                data-testid="start-project-schedule-btn"
              >
                Schedule Service
              </Button>
              <a
                href="tel:+19492040301"
                onClick={trackClickToCall}
                className="inline-flex items-center justify-center gap-2 px-6 py-4 border-2 border-white/30 text-white hover:bg-white/10 rounded-full text-lg font-semibold transition-colors whitespace-nowrap"
                data-testid="start-project-call-btn"
              >
                <Phone className="w-5 h-5" />
                (949) 204-0301
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-white/90 mb-8 text-lg max-w-2xl mx-auto">
            Request your free estimate and start earning rewards with every service.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {!user && (
              <Button
                size="lg"
                className="bg-white text-orange-600 hover:bg-gray-50 px-8 py-6 text-lg rounded-full shadow-lg font-semibold"
                onClick={onShowRegister}
                data-testid="cta-signup"
              >
                Create Free Account
              </Button>
            )}
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full font-semibold"
              onClick={() => navigate("/get-estimate")}
              data-testid="cta-estimate"
            >
              Get My Free Estimate
            </Button>
          </div>
          <p className="text-white/80 text-sm mt-6">Free estimates. No obligation.</p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
